import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { isKnownTab, Tab } from '@modules/common/types/tab';
import { ContactListStateService } from '@modules/contacts/services/contact-list-state.service';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { GroupListStateService } from '@modules/contacts/services/group-list-state.service';
import { GroupsService } from '@modules/contacts/services/groups.service';
import { ContactsAppStateService } from '@modules/contacts/services/state.service';
import { Contact } from '@modules/contacts/types/contact';
import { ContactsFilters } from '@modules/contacts/types/contacts-filters';
import { Group } from '@modules/contacts/types/group';
import { GroupsFilters } from '@modules/contacts/types/groups-filters';
import { ListState } from '@modules/contacts/types/list-state';
import { VirtualFolder } from '@modules/contacts/types/virtual-folder';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { combineLatest, of, Subject } from 'rxjs';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Application } from '@modules/common/types/application';

@Component({
  selector: 'app-contacts-list-container',
  templateUrl: './list-container.component.html',
  styleUrls: ['./list-container.component.less'],
  standalone: false,
  providers: [
    ...ContactListStateService.providers({
      stateKey: StateKey.contactsListState,
      withAdvancedSearch: true,
    }),
    ...GroupListStateService.providers({
      stateKey: StateKey.groupsListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class ContactsListContainerComponent implements OnInit, OnDestroy {
  public minimized: boolean;
  public searchQuery: string;
  public listState: ListState;
  public sidebarFilter: VirtualFolder;
  public quickContact: Contact;
  public quickGroup: Group;
  public listFilters: ContactsFilters;
  public loading: boolean;
  public tabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Groups', value: 'groups' },
    { title: 'Contacts', value: 'contacts' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'contacts';
  public groupsFilters: GroupsFilters;
  public contactsFilters: ContactsFilters;
  public listTenantItems: ReadonlyArray<{ readonly accountId?: string }> | undefined;
  public debug: 'score' = null;
  public containerName: string | null = null;

  // Private
  private alive = new Subject<void>();

  private readonly contactListStateService = inject(ContactListStateService, { self: true });
  private readonly groupListStateService = inject(GroupListStateService, { self: true });
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly contactsTenantScope = inject(ContactsTenantScopeService, { optional: true });
  private readonly selectedTenant$ = this.contactsTenantScope?.selectedTeamId?.() ?? of(null);
  private readonly contactsService = inject(ContactsService);
  private readonly groupsService = inject(GroupsService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.advancedSearchService.isAdvancedSearchApplied(Application.contacts),
    { initialValue: false },
  );

  constructor(
    private splitViewService: SplitViewService,
    private stateService: ContactsAppStateService,
    private modalService: ModalService,
    private searchService: AdvancedSearchService,
    private injector: Injector,
  ) {
    combineLatest([
      this.contactListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.selectedTenant$,
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone, tenantId]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.listTenantItems = tenantId ? [{ accountId: tenantId }] : undefined;
        this.contactsFilters = new ContactsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder, !!tenantId);
      });

    combineLatest([
      this.groupListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.selectedTenant$,
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone, tenantId]) => {
        this.listTenantItems = tenantId ? [{ accountId: tenantId }] : undefined;
        this.groupsFilters = new GroupsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.splitViewService
      .getMinimized('contactsList')
      .pipe(takeUntil(this.alive))
      .subscribe((minimized: boolean) => {
        this.minimized = minimized;
      });

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
        // Unconditional, not just on the non-'tabs' branch above: a container recreated by
        // stchSpaceRerender (e.g. on a space switch) re-inits `selectedTab` to its field default, but
        // the app-state service instance survives the switch and keeps whatever tab was last published
        // — republishing here on every emission, including while folder-mapped to 'tabs', overwrites
        // that stale value as soon as this container exists instead of only once the folder next
        // changes to a concrete one.
        this.stateService.setSelectedTab(this.selectedTab);
      });

    combineLatest([this.searchService.getState(), this.stateService.getVirtualFolder(), this.selectedTenant$])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, folder, tenantId]) => {
        this.sidebarFilter = folder;
        this.containerName = state[Application.contacts]?.containerName ?? null;
        this.quickContact = Contact.fromAdvancedState(state, folder);
        this.quickGroup = Group.fromAdvancedState(state, folder);
        this.quickContact.accountId = tenantId ?? undefined;
        this.quickGroup.accountId = tenantId ?? undefined;
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  protected handleSelectedTabChange(tab: string): void {
    if (!isKnownTab<Exclude<ListState, 'tabs'>>(tab, this.tabs)) {
      return;
    }
    this.selectedTab = tab;
    this.stateService.setSelectedTab(tab);
  }

  handleNewContact(contact?: Contact) {
    if (this.contactsTenantScope && !this.contactsFilters) return;
    this.stateService.setMainView(contact ?? this.quickContact ?? new Contact());
  }

  handleNewGroup(group?: Group) {
    if (this.contactsTenantScope && !this.groupsFilters) return;
    this.stateService.setMainView(group ?? this.quickGroup ?? new Group());
  }

  handleDoubleClickGroup(group: Group) {
    this.modalService.openFullForm(group, this.injector);
  }

  handleDoubleClickContact(contact: Contact) {
    this.modalService.openFullForm(contact, this.injector);
  }

  handleClickGroup(group: Group) {
    this.stateService.setMainView(group);
  }

  handleClickContact(contact: Contact) {
    this.stateService.setMainView(contact);
  }

  doDebug() {
    switch (this.debug) {
      case 'score':
        this.debug = null;
        break;
      default:
        this.debug = 'score';
    }
  }
}
